// Cloudinary configuration
const CLOUD_NAME = 'dpebevybp';
const BASE_URL = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload`;

// Image folder paths in Cloudinary (match your upload structure)
export const CloudinaryFolders = {
  BACKGROUNDS: 'jmnorheim/backgrounds',
  COMPANY_PICTURES: 'jmnorheim/company_pictures',
  COMPANY_LOGOS: 'jmnorheim/company_logos',
  PROJECT_PICTURES: 'jmnorheim/project_pictures',
  PROGRAMMING_LOGOS: 'jmnorheim/programming_logos',
  SKILLS_AND_INTERESTS: 'jmnorheim/skills_and_interests',
  CUSTOM_ELEMENTS: 'jmnorheim/custom_elements',
  CONTACT_ME: 'jmnorheim/contact_me_picture',
  LOGO: 'jmnorheim/logo',
} as const;

/**
 * Get an optimized Cloudinary image URL
 * @param folder - The Cloudinary folder path
 * @param imageName - The image filename (without extension)
 * @param options - Optional configuration for width, height, etc.
 */
export const getCloudinaryUrl = (
  folder: string,
  imageName: string,
  options?: {
    width?: number;
    height?: number;
  }
): string => {
  // Remove file extension if present
  const nameWithoutExt = imageName.replace(/\.(jpg|jpeg|png|webp|avif|svg|gif)$/i, '');
  
  // Build transformation string (q_auto:best for highest quality)
  const transforms = ['f_auto', 'q_auto:best'];
  
  if (options?.width) {
    transforms.push(`w_${options.width}`);
  }
  if (options?.height) {
    transforms.push(`h_${options.height}`);
  }
  
  const transformString = transforms.join(',');
  
  return `${BASE_URL}/${transformString}/${folder}/${nameWithoutExt}`;
};

/**
 * Get background image URL (full quality, no compression)
 */
export const getBackgroundUrl = (imageName: string, width?: number): string => {
  const nameWithoutExt = imageName.replace(/\.(jpg|jpeg|png|webp|avif|svg|gif)$/i, '');
  const transforms = ['f_auto', 'q_100'];
  if (width) {
    transforms.push(`w_${width}`);
  }
  return `${BASE_URL}/${transforms.join(',')}/${CloudinaryFolders.BACKGROUNDS}/${nameWithoutExt}`;
};

/**
 * Get company picture URL
 */
export const getCompanyPictureUrl = (imageName: string, width?: number): string => {
  return getCloudinaryUrl(CloudinaryFolders.COMPANY_PICTURES, imageName, { width });
};

/**
 * Get company logo URL
 */
export const getCompanyLogoUrl = (imageName: string, width?: number): string => {
  return getCloudinaryUrl(CloudinaryFolders.COMPANY_LOGOS, imageName, { width });
};

/**
 * Get project picture URL
 */
export const getProjectPictureUrl = (imageName: string, width?: number): string => {
  return getCloudinaryUrl(CloudinaryFolders.PROJECT_PICTURES, imageName, { width });
};

/**
 * Get programming/tech logo URL
 */
export const getTechLogoUrl = (imageName: string, width?: number): string => {
  return getCloudinaryUrl(CloudinaryFolders.PROGRAMMING_LOGOS, imageName, { width });
};

/**
 * Get skills and interests image URL
 */
export const getSkillsImageUrl = (imageName: string, width?: number): string => {
  return getCloudinaryUrl(CloudinaryFolders.SKILLS_AND_INTERESTS, imageName, { width });
};

/**
 * Get custom element URL (arrows, icons, etc.)
 */
export const getCustomElementUrl = (imageName: string, width?: number): string => {
  return getCloudinaryUrl(CloudinaryFolders.CUSTOM_ELEMENTS, imageName, { width });
};
